'use client';

import Link from 'next/link';
import type { MouseEvent, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import BookmarkButton from '@/components/predic/ui/BookmarkButton';
import { useFollowing } from '@/hooks/useFollowing';
import type { WorldCupMatchCardData, WorldCupMatchTeam } from '@/components/predic/types/market';
import { useTranslation } from '@/lib/i18n';
import { translateWorldCupTeamName } from '../teamTranslation';

export interface WorldCupMatchCardProps extends WorldCupMatchCardData {
  className?: string;
  onBookmarkClick?: () => void | Promise<void>;
  bookmarkLoading?: boolean;
  showBookmark?: boolean;
  showLiveBadge?: boolean;
  href?: string;
  linkEnabled?: boolean;
}

export function WorldCupMatchCard({
  slug,
  market_event_id,
  status_label,
  volume,
  draw_probability,
  bookmarked,
  home_team,
  away_team,
  className,
  onBookmarkClick,
  bookmarkLoading,
  showBookmark = true,
  href,
  linkEnabled = true,
}: WorldCupMatchCardProps) {
  const { t } = useTranslation();
  const { isFollowed, isFollowing, toggleFollow } = useFollowing(market_event_id, bookmarked);
  const bookmarkActive = market_event_id ? isFollowed : bookmarked;
  const isBookmarkLoading = bookmarkLoading ?? isFollowing;
  const resolvedHref = href ?? (slug ? `/predic/world-cup/${slug}` : '');

  async function handleBookmarkClick(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();

    if (onBookmarkClick) {
      await onBookmarkClick();
      return;
    }

    await toggleFollow();
  }

  const content = (
    <article
      className={cn(
        'relative flex min-h-27 w-full flex-col gap-3',
        'rounded-xl border border-[#1E1E1E] bg-[#121212] p-4 text-white',
        className,
      )}
    >
      <div className="flex justify-between">
        <div className="space-y-3">
          {status_label && <GameLabel label={status_label} />}
          <div className="flex h-10 w-full items-center gap-4">
            <TeamSummary team={home_team} />
            <span className="text-xl leading-6.5 text-white shrink-0">{t('vs')}</span>
            <TeamSummary team={away_team} />
          </div>
        </div>
        {showBookmark && (
          <BookmarkButton
            isFollowed={bookmarkActive}
            isFollowing={isBookmarkLoading}
            onClick={handleBookmarkClick}
            className="ml-auto hover:border-white/30 mt-1.5"
          />
        )}
      </div>

      <div className="flex h-5 w-full items-center justify-between gap-4 text-sm leading-5 text-[#757575]">
        <div className="flex min-w-0 items-center gap-2">
          <span className="capitalize">{t('world cup')}</span>
          <span className="h-4 w-px shrink-0 bg-[#3A3A3A]" />
          <span>
            {volume} {t('Vol')}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <span>{t('Draw')}:</span>
          <span>{draw_probability}</span>
        </div>
      </div>
    </article>
  );

  if (linkEnabled && resolvedHref) {
    return <Link href={resolvedHref}>{content}</Link>;
  }

  return content;
}

function LiveBadge({ children }: { children: ReactNode }) {
  return (
    <div className="self-start flex h-5 items-center justify-start gap-1 rounded-full bg-[#14C784]/20 px-2">
      <span className="size-1 rounded-full bg-[#14C784]" />
      <span className="text-sm font-medium leading-4 text-[#14C784]">{children}</span>
    </div>
  );
}

function GameLabel({ label }: { label: string }) {
  const { t } = useTranslation();

  if (label.toLowerCase() === 'live') {
    return <LiveBadge>{t(label)}</LiveBadge>;
  }

  return (
    <div className="text-sm font-medium leading-5 text-[#B7B9BC] capitalize h-5">{t(label)}</div>
  );
}

function TeamSummary({ team }: { team: WorldCupMatchTeam }) {
  const { t } = useTranslation();

  return (
    <div className="flex h-10 min-w-0 items-center gap-2 overflow-hidden">
      <TeamAvatar team={team} />
      <div className="flex h-10 min-w-0 flex-1 flex-col gap-1">
        <div
          className="w-full text-base font-medium leading-4.5 text-white truncate"
          title={translateWorldCupTeamName(team.name, t)}
        >
          {translateWorldCupTeamName(team.name, t)}
        </div>
        <div
          className={cn(
            'whitespace-nowrap text-xs font-normal leading-3.5',
            team.probability_class_name,
          )}
        >
          {team.probability}
        </div>
      </div>
    </div>
  );
}

function TeamAvatar({ team }: { team: WorldCupMatchTeam }) {
  if (team.image_src) {
    return (
      <img
        src={team.image_src}
        alt=""
        className="size-10 shrink-0 rounded-md object-contain"
        loading="lazy"
      />
    );
  }

  return (
    <span className="grid size-10 place-items-center rounded-md shrink-0 bg-[#24272D] text-xs font-semibold text-white">
      {team.avatar_fallback ?? team.name.slice(0, 2).toUpperCase()}
    </span>
  );
}
